import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, ApiError } from '../lib/api';

import { ACCOUNT_QUERY_KEY } from './accountQuery';

/**
 * Who is signed in, their plan, and what quota they have left this month.
 *
 * Always hits `/auth/me` (or serves the shared cache). Route guards, the shell
 * nav, and pricing CTAs all need the same answer; gating the query by path made
 * guest routes think the user was signed out while `/app` knew otherwise.
 *
 * A 401 is an answer, not a failure — signed out resolves to null. Never
 * retry that.
 */
export function useAccount() {
    return useQuery({
        queryKey: ACCOUNT_QUERY_KEY,
        queryFn: async () => {
            try {
                return await api.me();
            } catch (error) {
                if (error instanceof ApiError && error.status === 401) return null;
                throw error;
            }
        },
        retry: false,
        staleTime: 60_000,
    });
}

/**
 * Stripe's portal URL is single-use and short-lived, so it is fetched on click
 * rather than rendered into an href that goes stale while the page sits open.
 */
export function useBillingPortal() {
    return useMutation({
        mutationFn: () => api.billingPortal(),
        onSuccess: ({ url }) => {
            window.location.href = url;
        },
    });
}

/**
 * Hosted Checkout is equally short-lived. Client sends only the plan name; the
 * Worker maps it to a bound price id.
 */
export function useCheckout() {
    return useMutation({
        mutationFn: (plan: 'starter' | 'growth' | 'scale') => api.billingCheckout(plan),
        onSuccess: ({ url }) => {
            window.location.href = url;
        },
    });
}

/**
 * Waitlist signup. Used for paid CTAs only while `BILLING_LIVE` is false.
 *
 * No cache to invalidate, the list is write-only from the client's side, and
 * whether the row was new or already there doesn't change what the page shows.
 */
export function useJoinWaitlist() {
    return useMutation({
        mutationFn: ({ email, source }: { email: string; source?: string }) =>
            api.joinWaitlist(email, source),
    });
}

export function useLogout() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: () => api.logout(),
        onSuccess: () => client.clear(),
    });
}
